import { z } from "zod";

const EXPOSURE_STATUSES = [
  "Flat",
  "Unhedged",
  "OverHedged",
  "Unmapped",
  "MarkMissing",
  "VenueUnavailable",
  "ClientUnresolved",
] as const;

const ExposureStatusSchema = z.enum(EXPOSURE_STATUSES);

const nullableString = z.string().nullable();

const ExposureAccountNoteSchema = z.object({
  account_ref: z.string().nullish(),
  venue_name: z.string().nullish(),
  venue_account_id: z.string().nullish(),
  reason: z.string().nullish(),
  error_code: z.string().nullish(),
  error_message: z.string().nullish(),
});

const ExposureClientSchema = z.object({
  equity_swap_id: z.string(),
  counterparty_id: z.string(),
  counterparty_name: z.string(),
  margin_group_id: z.string(),
  direction: z.string(),
  number_of_shares: z.string(),
  signed_quantity: z.string(),
  unresolved_reason: nullableString,
});

const ExposureVenueAccountSchema = z.object({
  venue_account_id: z.string(),
  venue_name: z.string(),
  account_ref: z.string(),
  enabled: z.boolean(),
  state: z.enum(["live", "eod_snapshot", "venue_unavailable"]),
  size: nullableString,
  age_seconds: z.string().nullable(),
  as_of_date: nullableString,
  error_code: nullableString,
  error_message: nullableString,
});

const ExcludedIntercompanySchema = z.object({
  equity_swap_id: z.string(),
  client_counterparty_name: z.string(),
  signed_quantity: z.string(),
  reason: z.string(),
});

const UnmappedClientDeltaSchema = z.object({
  instrument_id: nullableString,
  instrument_name: nullableString,
  venue_symbol: nullableString,
  client_delta: z.string(),
  venue_net: nullableString,
  client_count: z.number().int(),
});

const GlobalExposureRowSchema = z.object({
  instrument_id: nullableString,
  instrument_name: nullableString,
  venue_symbol: nullableString,
  status: ExposureStatusSchema,
  client_delta: z.string(),
  venue_net: nullableString,
  residual: z.string(),
  mark: nullableString,
  mark_source: z.enum(["live", "eod_snapshot"]).nullable(),
  mark_age_seconds: z.string().nullable(),
  notional_usd: nullableString,
  floor_units: nullableString,
  venue_complete: z.boolean(),
  has_disabled_account: z.boolean(),
  intercompany_excluded_count: z.number(),
  clients: z.array(ExposureClientSchema),
  venue_accounts: z.array(ExposureVenueAccountSchema),
  excluded_intercompany: z.array(ExcludedIntercompanySchema),
});

const GlobalExposureSchema = z.object({
  generated_at: z.string(),
  instrument_id: nullableString,
  live_max_age_seconds: z.number(),
  notional_floor_usd: z.string(),
  lot_term_available: z.boolean(),
  rows: z.array(GlobalExposureRowSchema),
  unattributed_unavailable_accounts: z.array(ExposureAccountNoteSchema),
  skipped_venue_accounts: z.array(ExposureAccountNoteSchema),
  unmapped_instrument_count: z.number().int(),
  unmapped_client_delta_rows: z.array(UnmappedClientDeltaSchema),
});

type ExposureStatus = z.infer<typeof ExposureStatusSchema>;
type GlobalExposureView = z.infer<typeof GlobalExposureSchema>;
type GlobalExposureRow = z.infer<typeof GlobalExposureRowSchema>;
type ExposureAccountNote = z.infer<typeof ExposureAccountNoteSchema>;

export { ExposureStatusSchema, GlobalExposureSchema };
export type { ExposureAccountNote, ExposureStatus, GlobalExposureRow, GlobalExposureView };
